import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useMutation } from '@tanstack/react-query';
import { Container, Form, Button, Segment, Header, Grid, Message, Table, Dropdown, Checkbox, Input, Divider, Icon, Tab, Radio } from 'semantic-ui-react';
import { calculator, oils as oilsApi, recipes, additives as additivesApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { recipeSummary } from '../utils/calculator';
import RecipeSaveForm from '../components/recipe/RecipeSaveForm';

const soapTypes = [
  { key: 'naoh', value: 'naoh', text: 'Solid Soap - using NaOH (Sodium Hydroxide)' },
  { key: 'koh', value: 'koh', text: 'Liquid Soap - using KOH (Potassium Hydroxide)' },
  { key: 'hybrid', value: 'hybrid', text: 'Hybrid Soap - using both KOH and NaOH' },
];

const weightUnits = [
  { key: 'percent', value: 'percent', text: 'Percentages' },
  { key: 'g', value: 'g', text: 'Grams' },
  { key: 'kg', value: 'kg', text: 'Kilograms' },
  { key: 'lb', value: 'lb', text: 'Pounds' },
  { key: 'oz', value: 'oz', text: 'Ounces' },
];

const volumeUnits = [
  { key: 'g', value: 'g', text: 'Grams' },
  { key: 'kg', value: 'kg', text: 'Kilograms' },
  { key: 'lb', value: 'lb', text: 'Pounds' },
  { key: 'oz', value: 'oz', text: 'Ounces' },
];

const fragranceUnits = [
  { key: 'percent', value: 'percent', text: '% oil weight' },
  { key: 'gkg', value: 'gkg', text: 'g/kg' },
];

function Calculator() {
  const { user } = useAuth();
  const [soapType, setSoapType] = useState('naoh');
  const [naohPurity, setNaohPurity] = useState(100);
  const [kohPurity, setKohPurity] = useState(90);
  const [measureTab, setMeasureTab] = useState('weight');
  const [weightUnit, setWeightUnit] = useState('percent');
  const [weightValue, setWeightValue] = useState('');
  const [weightTotalUnit, setWeightTotalUnit] = useState('g');
  const [adjustOilWeights, setAdjustOilWeights] = useState(false);
  const [volumeUnit, setVolumeUnit] = useState('g');
  const [containerType, setContainerType] = useState('box');
  const [boxDims, setBoxDims] = useState({ width: '', height: '', length: '' });
  const [cylDims, setCylDims] = useState({ diameter: '', height: '' });
  const [waterType, setWaterType] = useState('percent');
  const [waterValue, setWaterValue] = useState('38');
  const [waterDiscount, setWaterDiscount] = useState('0');
  const [superfat, setSuperfat] = useState('5');
  const [superfatAfterCook, setSuperfatAfterCook] = useState(false);
  const [fragrance, setFragrance] = useState('3');
  const [fragranceUnit, setFragranceUnit] = useState('percent');
  const [additives, setAdditives] = useState([]);
  const [availableAdditives, setAvailableAdditives] = useState([]);
  const [additiveSearch, setAdditiveSearch] = useState('');
  const [recipeAdditives, setRecipeAdditives] = useState([]);
  const [oilSearch, setOilSearch] = useState('');
  const [availableOils, setAvailableOils] = useState([]);
  const [recipeOils, setRecipeOils] = useState([]);
  const [oilError, setOilError] = useState('');
  const [error, setError] = useState('');
  const [calcResult, setCalcResult] = useState(null);
  const [adjustLyeForCitric, setAdjustLyeForCitric] = useState(false);
  const [citricPercent, setCitricPercent] = useState(1);

  // Update defaults when soap type changes
  React.useEffect(() => {
    if (soapType === 'naoh') {
      setNaohPurity(100);
      setKohPurity(90);
      setWaterValue('38');
      setSuperfat('5');
    } else if (soapType === 'koh') {
      setNaohPurity(100);
      setKohPurity(90);
      setWaterValue('25');
      setSuperfat('3');
    } else if (soapType === 'hybrid') {
      setNaohPurity(100);
      setKohPurity(90);
      setWaterValue('33');
      setSuperfat('4');
    }
  }, [soapType]);

  // Fetch oils for search
  React.useEffect(() => {
    oilsApi.list({ search: oilSearch }).then(res => {
      setAvailableOils(res.data.data || []);
    });
  }, [oilSearch]);

  // Fetch additives for search
  React.useEffect(() => {
    additivesApi.list({ search: additiveSearch }).then(res => {
      setAvailableAdditives(res.data.data || []);
    });
  }, [additiveSearch]);

  // Add oil to recipe
  const addOil = (oil) => {
    if (recipeOils.find(o => o.id === oil.id)) return;
    setRecipeOils([...recipeOils, { ...oil, percentage: '', amount: '' }]);
    setOilError('');
  };

  // Remove oil from recipe
  const removeOil = (id) => {
    setRecipeOils(recipeOils.filter(o => o.id !== id));
  };

  // Update oil percentage/weight
  const updateOil = (id, value) => {
    const numeric = Number(value);
    setRecipeOils(recipeOils.map(o =>
      o.id === id
        ? weightUnit === 'percent'
          ? { ...o, percentage: isNaN(numeric) ? 0 : numeric }
          : { ...o, amount: isNaN(numeric) ? 0 : numeric }
        : o
    ));
  };

  // Add additive to recipe
  const addAdditive = (additive) => {
    if (recipeAdditives.find(a => a.id === additive.id)) return;
    setRecipeAdditives([...recipeAdditives, { ...additive, weight: '' }]);
  };

  // Remove additive from recipe
  const removeAdditive = (id) => {
    setRecipeAdditives(recipeAdditives.filter(a => a.id !== id));
  };

  // Update additive weight
  const updateAdditive = (id, value) => {
    const numeric = Number(value);
    setRecipeAdditives(recipeAdditives.map(a =>
      a.id === id
        ? { ...a, weight: isNaN(numeric) ? 0 : numeric }
        : a
    ));
  };

  // Handle form submit
  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    // Validation: at least one oil with a nonzero value
    const hasOil = recipeOils.some(o => {
      const val = weightUnit === 'percent' ? Number(o.percentage) : Number(o.amount);
      return val > 0;
    });
    if (!hasOil) {
      setOilError('At least one Oil is required');
      return;
    }

    let totalWeight = 0;
    let uom = weightUnit;
    let oilsForCalc = [];

    let recipeLyeConcentration = Number(waterValue);
    let lyeWaterWaterRatio = Number(waterValue);
    let lyeWaterLyeRatio = Number(waterValue);

    // Special parsing for Water:Lye Ratio
    if (waterType === 'water_lye_ratio') {
      const ratioParts = waterValue.split(':');
      if (ratioParts.length !== 2) {
        setError('Please enter Water:Lye Ratio in format "X:Y"');
        return;
      }
      const [waterPartStr, lyePartStr] = ratioParts;
      const waterPart = parseFloat(waterPartStr);
      const lyePart = parseFloat(lyePartStr);
      if (isNaN(waterPart) || isNaN(lyePart) || lyePart === 0) {
        setError('Invalid Water:Lye Ratio. Please enter numbers in format "2:1"');
        return;
      }
      lyeWaterLyeRatio = waterPart / lyePart;
    }

    if (measureTab === 'weight') {
      if (weightUnit === 'percent') {
        // Use total batch weight and entered percentages
        if (adjustOilWeights && weightValue && waterValue) {
          totalWeight = Number(weightValue) / (1 + (Number(waterValue) / 100));
        } else {
          totalWeight = Number(weightValue);
        }
        uom = weightTotalUnit;
        oilsForCalc = recipeOils.map(o => ({ id: o.id, weight: Number(o.percentage) || 0 }));
      } else {
        // Use sum of oil weights as total oil weight
        totalWeight = recipeOils.reduce((sum, o) => sum + (Number(o.amount) || 0), 0);
        uom = weightUnit;
        oilsForCalc = recipeOils.map(o => ({ id: o.id, weight: Number(o.amount) || 0 }));

        setWeightValue(totalWeight.toString());
      }
    } else if (measureTab === 'volume') {
      // By Volume: calculate oil weight from dimensions and density factor (legacy uses 0.95)
      let volume = 0;
      if (containerType === 'box') {
        const w = Number(boxDims.width) || 0;
        const h = Number(boxDims.height) || 0;
        const l = Number(boxDims.length) || 0;
        volume = w * h * l;
      } else if (containerType === 'cylinder') {
        const d = Number(cylDims.diameter) || 0;
        const h = Number(cylDims.height) || 0;
        volume = Math.PI * Math.pow(d / 2, 2) * h;
      }
      // Use density factor 0.95 and convert to selected unit
      const factor = { g: 1, kg: 1000, lb: 453.592, oz: 28.3495 }[volumeUnit] || 1;
      totalWeight = volume * 0.95 * factor;
      uom = volumeUnit;
      oilsForCalc = recipeOils.map(o => ({ id: o.id, weight: Number(weightUnit === 'percent' ? o.percentage : o.amount) || 0 }));
    }

    console.log('oilsForCalc', oilsForCalc);
    console.log('totalWeight', totalWeight);
    console.log('uom', uom);
    console.log('weightUnit', weightUnit);
    console.log('weightTotalUnit', weightTotalUnit);
    // Prepare recipeValues and oils for calculation
    const recipeValues = {
      soapType,
      naohPurity: Number(naohPurity),
      kohPurity: Number(kohPurity),
      uom: weightUnit === 'percent' ? 'percent' : uom,
      totalWeight: Number(totalWeight),
      totalsIncludeWater: adjustOilWeights,
      waterRatio: Number(waterValue), // If percent mode
      waterDiscount: Number(waterDiscount),
      superFat: Number(superfat),
      superfatAfter: superfatAfterCook,
      fragrance: Number(fragrance),
      fragranceType: fragranceUnit === 'percent' ? 'ratio' : 'ppo',
      fragrancePpo: Number(fragrance),
      lyeCalcType: waterType === 'lye_concentration' ? 'concentration'
                  : waterType === 'water_lye_ratio' ? 'ratio'
                  : 'lyewater',
      recipeLyeConcentration: Number(recipeLyeConcentration),
      lyeWaterWaterRatio: Number(lyeWaterWaterRatio),
      lyeWaterLyeRatio: Number(lyeWaterLyeRatio),
      ratioKoh: soapType === 'hybrid' ? 50 : soapType === 'koh' ? 100 : 0,
      ratioNaoh: soapType === 'hybrid' ? 50 : soapType === 'naoh' ? 100 : 0,
      enableCitricAdjust: adjustLyeForCitric,
      citricAdjustPercent: Number(citricPercent),
      oils: oilsForCalc.map(o => ({ id: o.id, weight: Number(o.weight) || 0 })),
      additives: recipeAdditives.map(a => ({ id: a.id, weight: Number(a.weight) || 0 })),
    };

    console.log('recipeValues', recipeValues);
    console.log('availableOils', availableOils);
    // Use availableOils as the oils list
    const raw = recipeSummary(recipeValues, availableOils);

    console.log('raw', raw);

    // For display: calculate oil percentages for non-percent mode
    let oilsList = [];
    if (weightUnit === 'percent') {
      oilsList = raw.display.recipeOilsWeightsRatios.map(({ oil, weight, ratio }) => ({
        id: oil.id,
        name: oil.name,
        weight: weight.toFixed(1),
        percent: (ratio * 100).toFixed(1),
      }));
    } else {
      const total = totalWeight || 1;
      oilsList = recipeOils.map(o => ({
        id: o.id,
        name: o.name,
        weight: Number(o.amount).toFixed(1),
        percent: ((Number(o.amount) / total) * 100).toFixed(1),
      }));
    }

    const calcResult = {
      total_oil_weight: raw.totals.totalOilWeight.toFixed(1),
      total_water_weight: raw.totals.totalWaterWeight.toFixed(1),
      total_naoh_weight: raw.totals.totalNaoh?.toFixed(1),
      total_koh_weight: raw.totals.totalKoh?.toFixed(1),
      total_batch_weight: raw.totals.totalBatchWeight.toFixed(1),
      fragrance_weight: raw.totals.fragranceWeight.toFixed(1),
      lye_concentration: raw.totals.lyeConcentration.toFixed(1) + '%',
      water_lye_ratio: raw.totals.waterLyeRatio?.toFixed(2),
      superfat: raw.totals.superfat,
      totalsIncludeWater: raw.display.totalsIncludeWater,
      sat_unsat_ratio: raw.saturations ? `${raw.saturations.saturated.toFixed(1)} / ${raw.saturations.unsaturated.toFixed(1)}` : 'N/A',
      properties: raw.properties,
      fatty_acids: raw.breakdowns,
      oils: oilsList
    };
    
    setCalcResult(calcResult);
  };

  // Refactored Block 2: Select recipe measures and weights
  const panes = [
    {
      menuItem: 'By weight',
      render: () => (
        <Tab.Pane attached={false}>
          <Form.Group inline>
            {weightUnits.map(opt => (
              <Form.Field key={opt.value}>
                <Radio
                  label={opt.text}
                  name="weightUnit"
                  value={opt.value}
                  checked={weightUnit === opt.value}
                  onChange={() => setWeightUnit(opt.value)}
                />
              </Form.Field>
            ))}
          </Form.Group>
          {weightUnit === 'percent' && (
            <Form.Group>
              <Form.Input
                label=""
                type="number"
                value={weightValue}
                onChange={e => setWeightValue(e.target.value)}
                placeholder="Total weight"
                min={0}
                style={{ width: 120 }}
              />
              <Form.Group inline style={{ marginLeft: 16 }}>
                {weightUnits.filter(u => u.value !== 'percent').map(opt => (
                  <Form.Field key={opt.value}>
                    <Radio
                      label={opt.text}
                      name="weightTotalUnit"
                      value={opt.value}
                      checked={weightTotalUnit === opt.value}
                      onChange={() => setWeightTotalUnit(opt.value)}
                    />
                  </Form.Field>
                ))}
              </Form.Group>
            </Form.Group>
          )}
          <Form.Field style={{ marginTop: 8 }}>
            <Checkbox
              label="Adjust oil weights to include water in Oils total"
              checked={adjustOilWeights}
              onChange={() => setAdjustOilWeights(!adjustOilWeights)}
            />
          </Form.Field>
          {adjustOilWeights && (
            <Message info size="tiny" style={{ marginTop: 4 }}>
              Less oil will be used as the lye calculator will adjust total oil weights to include water weight.
            </Message>
          )}
        </Tab.Pane>
      ),
    },
    {
      menuItem: 'By Volume',
      render: () => (
        <Tab.Pane attached={false}>
          <Form.Group inline>
            {volumeUnits.map(opt => (
              <Form.Field key={opt.value}>
                <Radio
                  label={opt.text}
                  name="volumeUnit"
                  value={opt.value}
                  checked={volumeUnit === opt.value}
                  onChange={() => setVolumeUnit(opt.value)}
                />
              </Form.Field>
            ))}
          </Form.Group>
          <Form.Group inline>
            <label>Container Type</label>
            <Form.Field>
              <Radio
                label="Box"
                name="containerType"
                value="box"
                checked={containerType === 'box'}
                onChange={() => setContainerType('box')}
              />
            </Form.Field>
            <Form.Field>
              <Radio
                label="Cylinder"
                name="containerType"
                value="cylinder"
                checked={containerType === 'cylinder'}
                onChange={() => setContainerType('cylinder')}
              />
            </Form.Field>
          </Form.Group>
          {containerType === 'box' ? (
            <>
              <Form.Group widths="equal">
                <Form.Input
                  label="Width (cm)"
                  type="number"
                  value={boxDims.width}
                  onChange={e => setBoxDims({ ...boxDims, width: e.target.value })}
                  min={0}
                  placeholder="Width"
                />
                <Form.Input
                  label="Height (cm)"
                  type="number"
                  value={boxDims.height}
                  onChange={e => setBoxDims({ ...boxDims, height: e.target.value })}
                  min={0}
                  placeholder="Height"
                />
              </Form.Group>
              <Form.Group>
                <Form.Input
                  label="Length (cm)"
                  type="number"
                  value={boxDims.length}
                  onChange={e => setBoxDims({ ...boxDims, length: e.target.value })}
                  min={0}
                  placeholder="Length"
                  style={{ width: '100%' }}
                />
              </Form.Group>
            </>
          ) : (
            <Form.Group widths="equal">
              <Form.Input
                label="Diameter (cm)"
                type="number"
                value={cylDims.diameter}
                onChange={e => setCylDims({ ...cylDims, diameter: e.target.value })}
                min={0}
                placeholder="Diameter"
              />
              <Form.Input
                label="Height (cm)"
                type="number"
                value={cylDims.height}
                onChange={e => setCylDims({ ...cylDims, height: e.target.value })}
                min={0}
                placeholder="Height"
              />
            </Form.Group>
          )}
        </Tab.Pane>
      ),
    },
  ];

  return (
    <>
      <Helmet>
        <title>Miakara Soap Recipes - Soap Calculator</title>
      </Helmet>
      <Container>
        <Segment vertical style={{ overflowX: 'auto' }}>
          <Header as="h1">Soap Calculator</Header>
          <Form onSubmit={handleSubmit}>
            <Grid stackable doubling columns={2}>
              {/* Soap Type & NaOH/KOH Purity */}
              <Grid.Column>
                <Header as="h4">1 - Liquid or Solid soap recipe?</Header>
                <Form.Group grouped>
                  {soapTypes.map(type => (
                    <Form.Radio
                      key={type.key}
                      label={type.text}
                      value={type.value}
                      checked={soapType === type.value}
                      onChange={() => setSoapType(type.value)}
                    />
                  ))}
                </Form.Group>
                {soapType === 'naoh' && (
                  <Form.Input
                    label="% NaOH Purity - recommended 100%"
                    type="number"
                    value={naohPurity}
                    onChange={e => setNaohPurity(e.target.value)}
                    min={0}
                    max={100}
                  />
                )}
                {soapType === 'koh' && (
                  <Form.Input
                    label="% KOH Purity - recommended 90%"
                    type="number"
                    value={kohPurity}
                    onChange={e => setKohPurity(e.target.value)}
                    min={0}
                    max={100}
                  />
                )}
                {soapType === 'hybrid' && (
                  <>
                    <Form.Input
                      label="% NaOH Purity - recommended 100%"
                      type="number"
                      value={naohPurity}
                      onChange={e => setNaohPurity(e.target.value)}
                      min={0}
                      max={100}
                    />
                    <Form.Input
                      label="% KOH Purity - recommended 90%"
                      type="number"
                      value={kohPurity}
                      onChange={e => setKohPurity(e.target.value)}
                      min={0}
                      max={100}
                    />
                  </>
                )}
              </Grid.Column>
              {/* Recipe Measures & Weights */}
              <Grid.Column>
                <Header as="h4">2 - Select recipe measures and weights</Header>
                <Tab
                  menu={{ secondary: true, pointing: true }}
                  panes={panes}
                  activeIndex={measureTab === 'weight' ? 0 : 1}
                  onTabChange={(_, data) => setMeasureTab(data.activeIndex === 0 ? 'weight' : 'volume')}
                />
              </Grid.Column>
            </Grid>
            <Divider />
            <Grid stackable doubling columns={2}>
              {/* Water Calculation */}
              <Grid.Column>
                <Header as="h4">3 - Amount of water in recipe</Header>
                <Form.Group grouped>
                  <Form.Radio
                    label="% Water as a percent of oils - recommended 33%-38%"
                    value="percent"
                    checked={waterType === 'percent'}
                    onChange={() => setWaterType('percent')}
                  />
                  <Form.Radio
                    label="% Lye Concentration"
                    value="lye_concentration"
                    checked={waterType === 'lye_concentration'}
                    onChange={() => {
                    setWaterType('lye_concentration');
                    setWaterValue('30');
                    }}
                  />
                  <Form.Radio
                    label="Water : Lye Ratio"
                    value="water_lye_ratio"
                    checked={waterType === 'water_lye_ratio'}
                    onChange={() => {
                      setWaterType('water_lye_ratio');
                      setWaterValue('3:1'); // Default ratio
                    }}
                  />
                </Form.Group>
                {waterType === 'percent' && (
                  <Form.Input
                    label="% Water"
                    type="number"
                    value={waterValue}
                    onChange={e => setWaterValue(e.target.value)}
                    min={0}
                    fluid
                  />
                )}
                {waterType === 'lye_concentration' && (
                  <Form.Input
                    label="% Lye Concentration"
                    type="number"
                    value={waterValue}
                    onChange={e => setWaterValue(e.target.value)}
                    min={0}
                    fluid
                  />
                )}
                {waterType === 'water_lye_ratio' && (
                  <Form.Group widths="equal">
                    <Form.Input
                      label="Water"
                      type="number"
                      value={waterValue?.split(':')[0] || ''}
                      onChange={e =>
                        setWaterValue(`${e.target.value || 0}:${waterValue?.split(':')[1] || 1}`)
                      }
                      min={0}
                      fluid
                    />
                    <Form.Input
                      label="Lye"
                      type="number"
                      value={waterValue?.split(':')[1] || ''}
                      onChange={e =>
                        setWaterValue(`${waterValue?.split(':')[0] || 1}:${e.target.value || 0}`)
                      }
                      min={0}
                      fluid
                    />
                  </Form.Group>
                )}
                <Form.Input
                  label="% Water Discount"
                  type="number"
                  value={waterDiscount}
                  onChange={e => setWaterDiscount(e.target.value)}
                  min={0}
                  fluid
                />
              </Grid.Column>
              {/* Oil superfat and Fragrances */}
              <Grid.Column>
                <Header as="h4">4 - Oil superfat</Header>
                <Form.Input
                  label="% superfat of oils - recommended <5%"
                  type="number"
                  value={superfat}
                  onChange={e => setSuperfat(e.target.value)}
                  min={0}
                  max={20}
                  fluid
                />
                <Checkbox
                  label="Superfat after cook"
                  checked={superfatAfterCook}
                  onChange={() => setSuperfatAfterCook(!superfatAfterCook)}
                />
                {superfatAfterCook && (
                  <Message info size="tiny" style={{ marginTop: 8 }}>
                    The recipe will be calculated with 0% superfat but will show the <strong>Fragrance Oil Weight</strong> to add to the recipe after cooking.
                  </Message>
                )}
                <Divider />
                <Header as="h4">5 - Fragrances</Header>
                <Form.Group inline widths="equal">
                  <Form.Input
                    type="number"
                    value={fragrance}
                    onChange={e => setFragrance(e.target.value)}
                    min={0}
                    fluid
                  />
                  <Dropdown
                    selection
                    options={fragranceUnits}
                    value={fragranceUnit}
                    onChange={(e, { value }) => setFragranceUnit(value)}
                    fluid
                  />
                </Form.Group>
              </Grid.Column>
            </Grid>
            <Divider />
            <Grid stackable columns={2}>
              {/* Additives and Calculation Tweaks Row */}
              <Grid.Column width={8}>
                <Header as="h4">6 - Select Additives</Header>
                {!user && (
                 <Message info size="tiny" style={{ marginTop: 8 }}>
                    Please login to create and manage your list of Soap Additives.
                  </Message>
                  
                )}
                {user && (
                  <>
                    <Input
                      icon="search"
                      placeholder="Filter Additives..."
                      value={additiveSearch}
                      onChange={e => setAdditiveSearch(e.target.value)}
                      fluid
                    />
                    <div style={{ maxHeight: 200, overflowY: 'auto', marginTop: 8 }}>
                      {availableAdditives.map(additive => (
                        <Segment key={additive.id} secondary clearing style={{ marginBottom: 4 }}>
                          {additive.name}
                          <Button
                            floated="right"
                            size="mini"
                            icon
                            onClick={() => addAdditive(additive)}
                            disabled={!!recipeAdditives.find(a => a.id === additive.id)}
                            title="Add Additive"
                          >
                            <Icon name="plus" />
                          </Button>
                        </Segment>
                      ))}
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <Table celled>
                        <Table.Header>
                          <Table.Row>
                            <Table.HeaderCell>Additive</Table.HeaderCell>
                            <Table.HeaderCell>Amount (g)</Table.HeaderCell>
                            <Table.HeaderCell></Table.HeaderCell>
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {recipeAdditives.map((additive) => (
                            <Table.Row key={additive.id}>
                              <Table.Cell>{additive.name}</Table.Cell>
                              <Table.Cell>
                                <Input
                                  type="number"
                                  value={additive.weight}
                                  onChange={e => updateAdditive(additive.id, e.target.value)}
                                  min={0}
                                  style={{ width: 100 }}
                                  placeholder="Amount"
                                />
                              </Table.Cell>
                              <Table.Cell>
                                <Button icon="trash" color="red" size="mini" onClick={() => removeAdditive(additive.id)} />
                              </Table.Cell>
                            </Table.Row>
                          ))}
                        </Table.Body>
                      </Table>
                    </div>
                  </>
                )}
              </Grid.Column>
              {/* Additives Calculation Tweaks */}
              <Grid.Column width={6}>
                <Segment>
                  <Header as="h5">Additives Calculation Tweaks</Header>
                  <Form.Group>
                    <Form.Field>
                      <Checkbox
                        label="Adjust Lye for Citric Acid"
                        checked={adjustLyeForCitric}
                        onChange={(_, data) => setAdjustLyeForCitric(data.checked)}
                      />
                    </Form.Field>
                  </Form.Group>
                  <Form.Group inline>
                    <Form.Input
                      type="number"
                      value={citricPercent}
                      onChange={e => setCitricPercent(Number(e.target.value))}
                      style={{ width: 80 }}
                      min={0}
                      step={0.1}
                    />
                    <label style={{ marginLeft: 8 }}>% of oil weight</label>
                  </Form.Group>
                </Segment>
              </Grid.Column>
            </Grid>
            <Divider />
            <Grid stackable columns={2}>
              {/* Oil Selection */}
              <Grid.Column width={8}>
                <Header as="h4">7 - Select Oils</Header>
                <Input
                  icon="search"
                  placeholder="Filter Oils..."
                  value={oilSearch}
                  onChange={e => setOilSearch(e.target.value)}
                  fluid
                />
                <div style={{ maxHeight: 200, overflowY: 'auto', marginTop: 8 }}>
                  {availableOils.map(oil => (
                    <Segment key={oil.id} secondary clearing style={{ marginBottom: 4 }}>
                      {oil.name}
                      <Button
                        floated="right"
                        size="mini"
                        icon
                        onClick={() => addOil(oil)}
                        disabled={!!recipeOils.find(o => o.id === oil.id)}
                        title="Add Oil"
                      >
                        <Icon name="plus" />
                      </Button>
                    </Segment>
                  ))}
                </div>
              </Grid.Column>
            </Grid>
            <Divider />
            {/* Recipe Oils Table */}
            <Header as="h4">Recipe Oils</Header>
            {oilError && <Message negative content={oilError} />}
            <div style={{ overflowX: 'auto' }}>
              <Table celled>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Oil</Table.HeaderCell>
                    <Table.HeaderCell>Amount ({weightUnit === 'percent' ? weightUnit : weightTotalUnit})</Table.HeaderCell>
                    <Table.HeaderCell></Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {recipeOils.map((oil) => (
                    <Table.Row key={oil.id}>
                      <Table.Cell>{oil.name}</Table.Cell>
                      <Table.Cell>
                        <Input
                          type="number"
                          value={weightUnit === 'percent' ? oil.percentage : oil.amount}
                          onChange={e => updateOil(oil.id, e.target.value)}
                          min={0}
                          style={{ width: 100 }}
                          placeholder={weightUnit === 'percent' ? 'Percent' : 'Amount'}
                        />
                      </Table.Cell>
                      <Table.Cell>
                        <Button icon="trash" color="red" size="mini" onClick={() => removeOil(oil.id)} />
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
            <Button type="submit" primary style={{ marginTop: 16 }}>
              Calculate
            </Button>
          </Form>
          {error && (
            <Message negative>
              <Message.Header>Error</Message.Header>
              <p>{error}</p>
            </Message>
          )}
          {calcResult && (
            <>
              <Segment>
                <Header as="h3">Results</Header>
                {/* Recipe Oils Table */}
                <Header as="h4">Recipe Oils</Header>
                <div style={{ overflowX: 'auto' }}>
                  <Table celled>
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell>Oil</Table.HeaderCell>
                        <Table.HeaderCell>%</Table.HeaderCell>
                        <Table.HeaderCell>Grams</Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {calcResult.oils && calcResult.oils.map((oil, idx) => (
                        <Table.Row key={oil.id || idx}>
                          <Table.Cell>{oil.name}</Table.Cell>
                          <Table.Cell>{oil.percent ?? oil.percentage ?? ''}</Table.Cell>
                          <Table.Cell>{oil.weight}</Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                </div>
                <Grid stackable columns={2}>
                  {/* Recipe Totals */}
                  <Grid.Column width={8}>
                    <Header as="h4">Recipe Totals</Header>
                    <Table basic="very" compact>
                    <Table.Body>
  {calcResult.totalsIncludeWater && (
    <Table.Row>
      <Table.Cell colSpan="2"><strong>Oil weight accounts for water</strong></Table.Cell>
    </Table.Row>
  )}
  <Table.Row>
    <Table.Cell>Total Water Weight</Table.Cell>
    <Table.Cell>{calcResult.total_water_weight ?? calcResult.water}g</Table.Cell>
  </Table.Row>
  <Table.Row>
    <Table.Cell>Total NaOH Weight</Table.Cell>
    <Table.Cell>{calcResult.total_naoh_weight ?? ''}g</Table.Cell>
  </Table.Row>
  <Table.Row>
    <Table.Cell>Total KOH Weight</Table.Cell>
    <Table.Cell>{calcResult.total_koh_weight ?? ''}g</Table.Cell>
  </Table.Row>
  <Table.Row>
    <Table.Cell>Total Oil Weight</Table.Cell>
    <Table.Cell>{calcResult.total_oil_weight ?? ''}g</Table.Cell>
  </Table.Row>
  <Table.Row>
    <Table.Cell>Fragrance (g)</Table.Cell>
    <Table.Cell>{calcResult.fragrance_weight ?? ''}</Table.Cell>
  </Table.Row>
  <Table.Row>
    <Table.Cell>Total Batch Weight</Table.Cell>
    <Table.Cell>{calcResult.total_batch_weight ?? ''}g</Table.Cell>
  </Table.Row>
  <Table.Row>
    <Table.Cell>Superfat</Table.Cell>
    <Table.Cell>{calcResult.superfat ?? ''}%</Table.Cell>
  </Table.Row>
  <Table.Row>
    <Table.Cell>Water:Lye Ratio</Table.Cell>
    <Table.Cell>{calcResult.water_lye_ratio ?? ''}</Table.Cell>
  </Table.Row>
  <Table.Row>
    <Table.Cell>Lye Concentration</Table.Cell>
    <Table.Cell>{calcResult.lye_concentration ?? ''}</Table.Cell>
  </Table.Row>
  <Table.Row>
    <Table.Cell>Saturated:Unsaturated</Table.Cell>
    <Table.Cell>{calcResult.sat_unsat_ratio ?? ''}</Table.Cell>
  </Table.Row>
</Table.Body>
                    </Table>
                  </Grid.Column>
                  {/* Recipe Properties & Fatty Acids */}
                  <Grid.Column width={8}>
                    <Header as="h4">Recipe Properties</Header>
                    <Table basic="very" compact>
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell>Property</Table.HeaderCell>
                          <Table.HeaderCell>%</Table.HeaderCell>
                          <Table.HeaderCell>Recommended</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        <Table.Row>
                          <Table.Cell>Bubbly</Table.Cell>
                          <Table.Cell>{calcResult.properties?.bubbly ?? ''}</Table.Cell>
                          <Table.Cell>14 - 46</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell>Cleansing</Table.Cell>
                          <Table.Cell>{calcResult.properties?.cleansing ?? ''}</Table.Cell>
                          <Table.Cell>12 - 22</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell>Condition</Table.Cell>
                          <Table.Cell>{calcResult.properties?.condition ?? ''}</Table.Cell>
                          <Table.Cell>44 - 69</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell>Hardness</Table.Cell>
                          <Table.Cell>{calcResult.properties?.hardness ?? ''}</Table.Cell>
                          <Table.Cell>29 - 54</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell>Longevity</Table.Cell>
                          <Table.Cell>{calcResult.properties?.longevity ?? ''}</Table.Cell>
                          <Table.Cell>25 - 50</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell>Stable</Table.Cell>
                          <Table.Cell>{calcResult.properties?.stability ?? ''}</Table.Cell>
                          <Table.Cell>16 - 48</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell>Iodine</Table.Cell>
                          <Table.Cell>{calcResult.properties?.iodine ?? ''}</Table.Cell>
                          <Table.Cell>41 - 70</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell>INS</Table.Cell>
                          <Table.Cell>{calcResult.properties?.ins ?? ''}</Table.Cell>
                          <Table.Cell>136 - 165</Table.Cell>
                        </Table.Row>
                      </Table.Body>
                    </Table>
                    <Header as="h4">Fatty Acids %</Header>
                    <Table basic="very" compact>
                      <Table.Body>
                        <Table.Row>
                          <Table.Cell>Linoleic</Table.Cell>
                          <Table.Cell>{calcResult.fatty_acids?.linoleic ?? ''}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell>Linolenic</Table.Cell>
                          <Table.Cell>{calcResult.fatty_acids?.linolenic ?? ''}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell>Oleic</Table.Cell>
                          <Table.Cell>{calcResult.fatty_acids?.oleic ?? ''}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell>Palmitic</Table.Cell>
                          <Table.Cell>{calcResult.fatty_acids?.palmitic ?? ''}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell>Stearic</Table.Cell>
                          <Table.Cell>{calcResult.fatty_acids?.stearic ?? ''}</Table.Cell>
                        </Table.Row>
                      </Table.Body>
                    </Table>
                  </Grid.Column>
                </Grid>
              </Segment>
              {/* Recipe Save Section */}
              <Segment>
                <Header as="h3">Want to save your Recipe?</Header>
                {user ? (
                  <RecipeSaveForm result={calcResult} recipeOils={recipeOils} />
                ) : (
                  <Message warning content="Please login to save your recipe and manage your recipes." />
                )}
              </Segment>
              {/* Add Recipe Additives to Results */}
              {calcResult && recipeAdditives.length > 0 && (
                <>
                  <Header as="h4">Recipe Additives</Header>
                  <div style={{ overflowX: 'auto' }}>
                    <Table celled>
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell>Additive</Table.HeaderCell>
                          <Table.HeaderCell>Amount (g)</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {recipeAdditives.map((additive) => (
                          <Table.Row key={additive.id}>
                            <Table.Cell>{additive.name}</Table.Cell>
                            <Table.Cell>{additive.weight}</Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  </div>
                </>
              )}
            </>
          )}
        </Segment>
      </Container>
    </>
  );
}

export default Calculator; 